import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Home as HomeIcon, Loader2 } from "lucide-react";

export default function AuthLanding() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
      toast.success(`Welcome${u.name ? ", " + u.name.split(" ")[0] : ""}!`);
      navigate(u.onboarded ? "/home" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-ink">
      <div className="w-full overflow-hidden bg-[#F3ECFB]">
        <img src="/cover.png" alt="FlatTribe — Find your place. Find your people." className="h-auto w-full object-contain" />
      </div>

      <div className="rounded-t-[2rem] bg-background px-6 pb-10 pt-7 -mt-6 relative">
        <div className="mb-5 flex rounded-full bg-secondary p-1">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`auth-tab-${m}`}
              onClick={() => setMode(m)}
              className={`flex-1 rounded-full py-2.5 text-sm font-bold capitalize transition-colors ${mode === m ? "bg-white text-ink shadow-soft" : "text-ink-soft"}`}
            >
              {m === "login" ? "Log in" : "Sign up"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <Label className="text-ink-soft">Full name</Label>
              <Input data-testid="auth-name" value={form.name} onChange={set("name")} required placeholder="Aarav Mehta" className="mt-1 h-12 rounded-2xl border-border bg-white" />
            </div>
          )}
          <div>
            <Label className="text-ink-soft">Email</Label>
            <Input data-testid="auth-email" type="email" value={form.email} onChange={set("email")} required placeholder="you@email.com" className="mt-1 h-12 rounded-2xl border-border bg-white" />
          </div>
          {mode === "register" && (
            <div>
              <Label className="text-ink-soft">Phone</Label>
              <Input data-testid="auth-phone" value={form.phone} onChange={set("phone")} placeholder="+91 98xxxxxxxx" className="mt-1 h-12 rounded-2xl border-border bg-white" />
            </div>
          )}
          <div>
            <Label className="text-ink-soft">Password</Label>
            <Input data-testid="auth-password" type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" className="mt-1 h-12 rounded-2xl border-border bg-white" />
          </div>
          <Button data-testid="auth-submit" type="submit" disabled={busy} className="h-12 w-full rounded-full bg-brand text-base font-bold text-white shadow-brand hover:bg-brand-dark">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "login" ? "Log in" : "Create account"}
          </Button>
        </form>

        <div className="my-5 flex items-center gap-3 text-xs text-ink-soft">
          <span className="h-px flex-1 bg-border" /> or continue with <span className="h-px flex-1 bg-border" />
        </div>

        <Button data-testid="auth-google" onClick={google} variant="outline" className="h-12 w-full rounded-full border-border bg-white text-base font-bold text-ink hover:bg-secondary">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="mr-2 h-5 w-5" /> Google
        </Button>

        <p className="mt-6 text-center text-xs text-ink-soft">By continuing you agree to keep FlatTribe safe & respectful.</p>
      </div>
    </div>
  );
}
